import { Modal } from "@kamae-apps/shared/Components/Molecules/Modal/Standard/Standard"
import { FC } from "react"
import { createPortal } from "react-dom"

enum SEND_STATE {
  SENT = "✅",
  BLOCKED = "❌",
  NOT_APPLICABLE = "—",
}

type GatedSending = {
  label: string
  note?: string
  disabled: SEND_STATE
  enabledDuringOnboarding: SEND_STATE
  enabledAfterOnboarding: SEND_STATE
}

// Mirrors the backend gates on automatedEmailEnabled + onboardingCompleted (EmailSenderImpl, CampaignService,
// SetTeamLeaderUseCase, CompanyRoute onboarding closure, monthly report query): update it when one of them changes.
const GATED_SENDINGS: GatedSending[] = [
  {
    label: "📩 Invitations collaborateurs",
    note: "ajout unitaire, import CSV, SCIM",
    disabled: SEND_STATE.BLOCKED,
    enabledDuringOnboarding: SEND_STATE.BLOCKED,
    enabledAfterOnboarding: SEND_STATE.SENT,
  },
  {
    label: "🚪 Invitations à l'ouverture du dojo",
    note: "si l'étape « invitations » est confirmée",
    disabled: SEND_STATE.BLOCKED,
    enabledDuringOnboarding: SEND_STATE.SENT,
    enabledAfterOnboarding: SEND_STATE.NOT_APPLICABLE,
  },
  {
    label: "👋 Bienvenue des admins",
    disabled: SEND_STATE.BLOCKED,
    enabledDuringOnboarding: SEND_STATE.SENT,
    enabledAfterOnboarding: SEND_STATE.SENT,
  },
  {
    label: "🧭 E-mail chefs d'équipe",
    disabled: SEND_STATE.BLOCKED,
    enabledDuringOnboarding: SEND_STATE.BLOCKED,
    enabledAfterOnboarding: SEND_STATE.SENT,
  },
  {
    label: "📊 Bilan mensuel",
    note: "e-mail, Slack, Teams · hors offre Starter",
    disabled: SEND_STATE.BLOCKED,
    enabledDuringOnboarding: SEND_STATE.BLOCKED,
    enabledAfterOnboarding: SEND_STATE.SENT,
  },
  {
    label: "🎣 Fin de campagne de phishing",
    note: "e-mail, Slack, Teams",
    disabled: SEND_STATE.BLOCKED,
    enabledDuringOnboarding: SEND_STATE.BLOCKED,
    enabledAfterOnboarding: SEND_STATE.SENT,
  },
]

const ALWAYS_SENT = [
  "Campagnes de phishing",
  "Rappels envoyés par l'admin",
  "Rappels de défis Slack / Teams",
  "Rapport d'import CSV",
]

type AutomatedEmailInfoProps = {
  display: boolean
  close: () => void
}

export const AutomatedEmailInfo: FC<AutomatedEmailInfoProps> = ({ display, close }) => {
  // Portal to body: rendered inline, the modal sits in the column stacking context and page dropdowns overlap it
  return createPortal(
    <Modal
      variant={"modal-md"}
      display={display}
      title={"Que pilote « Emails automatiques » ?"}
      close={close}
    >
      <div className={"flex w-full flex-col gap-4 pb-6 body-sm-regular"}>
        <table className={"w-full border-collapse"}>
          <thead>
            <tr className={"border-b border-neutral-gray-200 text-center caption-semibold text-neutral-gray-600"}>
              <th className={"w-1/2 py-2 text-left"}>Envoi</th>
              <th className={"px-2 py-2"}>🔕 Décochée</th>
              <th className={"px-2 py-2"}>
                🔔 Cochée
                <br />
                onboarding en cours
              </th>
              <th className={"px-2 py-2"}>
                🔔 Cochée
                <br />
                onboarding terminé
              </th>
            </tr>
          </thead>
          <tbody>
            {GATED_SENDINGS.map(sending => (
              <tr
                key={sending.label}
                className={"border-b border-neutral-gray-100"}
              >
                <td className={"py-1.5"}>
                  <div className={"flex flex-col"}>
                    <span className={"body-sm-semibold"}>{sending.label}</span>
                    {sending.note && <span className={"caption-regular text-neutral-gray-500"}>{sending.note}</span>}
                  </div>
                </td>
                <td className={"text-center"}>{sending.disabled}</td>
                <td className={"text-center"}>{sending.enabledDuringOnboarding}</td>
                <td className={"text-center"}>{sending.enabledAfterOnboarding}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={"flex flex-col gap-2 rounded-lg bg-primary-success-50 p-3"}>
          <span className={"body-sm-semibold"}>✅ Toujours envoyés, quelle que soit l'option</span>
          <div className={"flex flex-wrap gap-2"}>
            {ALWAYS_SENT.map(sending => (
              <span
                key={sending}
                className={"rounded-full bg-white px-3 py-1 caption-regular"}
              >
                {sending}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Modal>,
    document.body
  )
}
